import { MapPin } from "lucide-react"
import { usePage } from "@inertiajs/react"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

export interface LocationCard {
  id: number
  name: string
  slug: string
  region: string
  address: string
  maps_url: string | null
  primary_photo_url: string | null
  /** The location's own page. */
  href: string
}

// The card grid the home page, Rent a Room and the About page share: one
// card per location, its home-hero photo over the region and address, each
// to the location's own page.
export default function LocationCards({ sites }: { sites: LocationCard[] }) {
  const { translations } = usePage<PageProps>().props
  const t = translations.home.locations
  const lp = useLocalePath()

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
      {sites.map((site) => (
        <a
          key={site.id}
          href={lp(site.href)}
          className="group overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-hairline transition-shadow hover:shadow-md"
        >
          <div className="relative h-56 overflow-hidden bg-stone">
            {site.primary_photo_url ? (
              <img
                src={site.primary_photo_url}
                alt={site.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-sm text-ink-muted">{site.name}</span>
              </div>
            )}
          </div>
          <div className="p-6">
            <h3>{site.region}</h3>
            <p className="mt-2 flex items-start gap-1.5 text-sm text-ink-muted">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {site.address}
            </p>
            <p className="mt-4 text-sm font-medium text-teal-mid">
              {t.learn_more}
            </p>
          </div>
        </a>
      ))}
    </div>
  )
}
